const DEFAULT_CURFEW_START_UTC = 7;
const DEFAULT_CURFEW_END_UTC = 16;

const resolveHour = (value: string | undefined, fallback: number) => {
  if (!value || value.trim() === '') return fallback;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return parsed;
};

const CONFIGURED_BLOCK_START_UTC = resolveHour(process.env.REACT_APP_SPOTIFY_BLOCK_START_UTC, DEFAULT_CURFEW_START_UTC);
const CONFIGURED_BLOCK_END_UTC = resolveHour(process.env.REACT_APP_SPOTIFY_BLOCK_END_UTC, DEFAULT_CURFEW_END_UTC);
const BLOCKING_DISABLED =
  process.env.REACT_APP_DISABLE_SPOTIFY_BLOCK === 'true' ||
  process.env.REACT_APP_DISABLE_SPOTIFY_BLOCK === '1';

const normalizeHour = (value: number) => {
  if (Number.isNaN(value)) return 0;
  return Math.min(23, Math.max(0, Math.floor(value)));
};

const shouldBlock = (date: Date) => {
  const start = normalizeHour(CONFIGURED_BLOCK_START_UTC);
  const end = normalizeHour(CONFIGURED_BLOCK_END_UTC);
  const hour = date.getUTCHours();

  if (start === end) {
    return false;
  }

  if (start < end) {
    return hour >= start && hour < end;
  }

  // Window wraps around midnight (e.g. 22 -> 3)
  return hour >= start || hour < end;
};

type SpotifyCurfewOptions = {
  date?: Date;
  reason?: string;
};

/**
 * Determines whether the frontend is allowed to call Spotify directly
 * based on a UTC time window. By default, requests are blocked between
 * 07:00 and 15:59 UTC to avoid overlapping with the artist pipeline.
 */
export function isSpotifyRequestAllowed(options: SpotifyCurfewOptions = {}): boolean {
  if (BLOCKING_DISABLED) {
    return true;
  }

  const date = options.date ?? new Date();
  const blocked = shouldBlock(date);
  if (blocked && typeof console !== 'undefined') {
    const reason = options.reason ? ` (${options.reason})` : '';
    const hour = date.getUTCHours();
    const minute = date.getUTCMinutes().toString().padStart(2, '0');
    console.info(
      `[SpotifyCurfew] blocked request${reason} at ${hour}:${minute} UTC (window ${normalizeHour(
        CONFIGURED_BLOCK_START_UTC
      )
        .toString()
        .padStart(2, '0')}:00-${normalizeHour(CONFIGURED_BLOCK_END_UTC)
        .toString()
        .padStart(2, '0')}:00)`
    );
  }
  return !blocked;
}
